import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from "react";

export function Button({
  children,
  variant = "primary",
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
}) {
  return (
    <button className={`button button--${variant} ${className}`} {...props}>
      {children}
    </button>
  );
}
export function Icon({
  name,
}: {
  name:
    | "spark"
    | "search"
    | "calendar"
    | "user"
    | "map"
    | "bell"
    | "back"
    | "location"
    | "lock"
    | "star"
    | "clock"
    | "car"
    | "check"
    | "card";
}) {
  if (name === "bell")
    return (
      <span className="icon" aria-hidden="true">
        <svg
          viewBox="0 0 24 24"
          width="22"
          height="22"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9Z" />
          <path d="M10 21h4" />
        </svg>
      </span>
    );
  const glyph = {
    spark: "✦",
    search: "⌕",
    calendar: "▣",
    user: "◉",
    map: "⌖",
    back: "‹",
    location: "⌖",
    lock: "◆",
    star: "★",
    clock: "◷",
    car: "→",
    check: "✓",
    card: "▰",
  }[name];
  return (
    <span className="icon" aria-hidden="true">
      {glyph}
    </span>
  );
}
export function Tag({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "neutral" | "accent" | "success" | "warning" | "danger";
}) {
  return <span className={`tag tag--${tone}`}>{children}</span>;
}
export function Card({
  children,
  className = "",
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={`card ${className}`} {...props}>
      {children}
    </div>
  );
}
export function Price({ band }: { band: number }) {
  return (
    <span aria-label={`${band} of 4 price level`}>
      <strong>{"$".repeat(band)}</strong>
      <span className="muted">{"$".repeat(4 - band)}</span>
    </span>
  );
}
export function Rating({ value }: { value: number }) {
  return (
    <span className="rating">
      <Icon name="star" /> {value.toFixed(1)}
    </span>
  );
}
export function Skeleton({ height = 80 }: { height?: number }) {
  return <div className="skeleton" style={{ height }} aria-label="Loading" />;
}
export function StatePanel({
  icon = "✦",
  title,
  body,
  action,
}: {
  icon?: string;
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="state-panel">
      <span className="state-panel__icon" aria-hidden>
        {icon}
      </span>
      <h2>{title}</h2>
      <p>{body}</p>
      {action}
    </div>
  );
}
export function PageHeader({
  title,
  eyebrow,
  action,
  back,
}: {
  title: string;
  eyebrow?: string;
  action?: ReactNode;
  back?: () => void;
}) {
  return (
    <header className="page-header">
      {back && (
        <button className="icon-button" onClick={back} aria-label="Go back">
          <Icon name="back" />
        </button>
      )}
      <div className="page-header__copy">
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h1>{title}</h1>
      </div>
      {action}
    </header>
  );
}
